import type {
  GuardianContextKind,
  GuardianScriptedReply,
} from "@/api/temp/TrustAIExecutiveService";
import {
  NOT_EVALUATED_AGENT_COUNT,
  RUNTIME_ACCESS_POLICY_NAME,
} from "./seed";

/**
 * Guardian's scripted conversation, in one place.
 *
 * Every sentence Guardian says on the board comes from this file — the drawer
 * renders what it is given and never assembles copy at the call site, so the
 * demo narrative can be rewritten without touching a component.
 *
 * Keyed by `GuardianContext.key`. Category keys are `${dimension}:${value}`,
 * which is exactly what `AgentEstateStore.getSummary` already emits as
 * `group.key`, so a card looks up its script with no translation table. The
 * estate is small in the seed but the real one is not, so `DEFAULT_BY_KIND`
 * answers every subject this file does not name individually.
 */
export interface GuardianScriptPrompt {
  id: string;
  prompt: string;
  answer: string;
}

export interface GuardianScript {
  /** Shown right after the context marker, before anything is asked. */
  opening: string;
  prompts: GuardianScriptPrompt[];
  /** Used when free text matches none of the prompts above. */
  fallback: string;
}

const ESTATE_SCRIPT: GuardianScript = {
  opening: [
    "I can help you understand your AI estate.",
    "",
    `TrustLogix is tracking **612 agents** across 15 data platforms. **520** satisfy their required access policies, **69** have policy gaps, and **${NOT_EVALUATED_AGENT_COUNT}** cannot be evaluated yet.`,
    "",
    "The fastest way in is usually the agents that have never been evaluated — they carry the most unknown exposure.",
  ].join("\n"),
  prompts: [
    {
      id: "estate-focus",
      prompt: "What should I focus on first?",
      answer: [
        `Start with the **${NOT_EVALUATED_AGENT_COUNT} agents awaiting evaluation**. They account for roughly 68% of current high-risk exposure, and unlike the 69 with known gaps, you do not yet know what they can reach.`,
        "",
        `The recommended policy is **${RUNTIME_ACCESS_POLICY_NAME}**, which scopes runtime access for exactly that cohort.`,
      ].join("\n"),
      // Guardian names the policy; entering the workflow stays the user's move.
    },
    {
      id: "estate-platforms",
      prompt: "Which platforms have the most agents?",
      answer:
        "Microsoft Copilot and OpenAI carry the largest agent populations, followed by Amazon Bedrock and Databricks. Switch the **Explore** section to the AI Platform lens to see the split for your connected environment.",
    },
    {
      id: "estate-unevaluated",
      prompt: "Which agents have not been evaluated?",
      answer: `${NOT_EVALUATED_AGENT_COUNT} agents are still awaiting evaluation — newly discovered, identity unresolved, data mapping incomplete, or evaluation pending. Select **Not Evaluated** on the posture bar to list them with the outstanding step for each.`,
    },
    {
      id: "estate-gaps",
      prompt: "Where are the largest policy gaps?",
      answer:
        "Cross-border regulated data access is the single largest gap, followed by over-authorized access to sensitive financial data. Both appear in **Requires Attention** with their recommended policies.",
    },
  ],
  fallback:
    "I can speak to the size of the estate, where policy gaps concentrate, which agents have not been evaluated, and what to address first. Ask about any of those, or select Ask Guardian on a category to narrow what I am reasoning about.",
};

/**
 * Guardian during onboarding, one script per lifecycle state.
 *
 * Before there is an estate, Guardian cannot answer "what did you find" — so
 * these answer "what should I connect, and what will you do with it" instead.
 * None of them claim anything about the reader's security posture, because
 * nothing has been evaluated: an unevaluated estate is not a safe one, and
 * Guardian is the last place that should blur that.
 *
 * Keyed `setup:<LIFECYCLE_STATE>`, so each state's suggested chips come
 * straight out of `listGuardianPromptsUsingGet` with no extra plumbing.
 */
const SETUP_ZERO_DAY_SCRIPT: GuardianScript = {
  opening: [
    "I can help you get started.",
    "",
    "TrustLogix has not discovered anything yet — there are no platforms connected, so there is no estate for me to reason about. Connecting the first platform is what starts discovery.",
    "",
    "Ask me what to connect, how discovery works, or what evaluation will check.",
  ].join("\n"),
  prompts: [
    {
      id: "setup-connect-first",
      prompt: "What should I connect first?",
      answer: [
        "Start with the **agent registry where your agents actually run** — Azure AI Foundry, for instance. That is what gives TrustLogix the agent inventory everything else hangs off.",
        "",
        "Connect enterprise data platforms next (SharePoint, Snowflake, Databricks), so the agents can be mapped to the data they reach. MCP servers and agent tools can follow once agents are known.",
        "",
        "Access is read-only until you choose to apply a policy.",
      ].join("\n"),
    },
    {
      id: "setup-how-discovery",
      prompt: "How does TrustAI discover agents?",
      answer: [
        "Once a platform is connected, TrustLogix reads its agent definitions and metadata, then analyzes each agent's prompts and capabilities to infer what it is for.",
        "",
        "Nothing is changed on the platform during discovery — it reads, classifies and builds an inventory. You can leave the page while it runs.",
      ].join("\n"),
    },
    {
      id: "setup-how-evaluation",
      prompt: "How will my agents be evaluated?",
      answer: [
        "Evaluation walks each access path — **identity → agent → tool or service → data** — and compares it against your governance controls.",
        "",
        "It reports three things: policy gaps, risky access, and violations. Until it has run, an agent is **Not Evaluated**, which is not the same as secure — it means nothing has judged it yet.",
      ].join("\n"),
    },
  ],
  fallback:
    "I can tell you what to connect first, how discovery works, and what evaluation will check. I cannot tell you anything about your agents yet — nothing has been discovered.",
};

const SETUP_DISCOVERING_SCRIPT: GuardianScript = {
  opening: [
    "Discovery is running.",
    "",
    "I can tell you what has been found so far and what is still outstanding. Counts will keep moving until the run finishes, so treat anything at zero as *not looked at yet* rather than as a total.",
  ].join("\n"),
  prompts: [
    {
      id: "setup-discovered-so-far",
      prompt: "What has TrustAI discovered?",
      answer:
        "The **Enterprise AI & Data Estate** row above holds the current counts. Agents and data sources are coming in first; MCP servers and tools are read once the agents that use them are known, which is why they still read *Not discovered yet*.",
    },
    {
      id: "setup-connect-next",
      prompt: "What should I connect next?",
      answer:
        "Add the enterprise data platforms your agents reach — SharePoint, Snowflake, Databricks. Without them an agent's access path stops at the tool and TrustLogix cannot tell you which data it ends at.",
    },
    {
      id: "setup-missing-systems",
      prompt: "Are any important systems missing?",
      answer:
        "I can only speak to what is connected. If your agents use MCP servers or an MCP Access Gateway that is not in the list above, those paths are invisible to TrustLogix right now — connect the agent registry they run on and discovery will pick them up.",
    },
  ],
  fallback:
    "While discovery runs I can tell you what has been found, what is still outstanding, and what else is worth connecting.",
};

const SETUP_DISCOVERED_SCRIPT: GuardianScript = {
  opening: [
    "Your estate is discovered, but nothing has been evaluated yet.",
    "",
    "That means I can tell you what exists and how it is connected, but not whether any of it is secure. **Not Evaluated** is a genuine unknown, not a clean bill of health.",
    "",
    "Starting an evaluation is what produces policy gaps, risky access and violations.",
  ].join("\n"),
  prompts: [
    {
      id: "setup-evaluate-first",
      prompt: "Which agents should I evaluate first?",
      answer:
        "Evaluate the whole discovered estate in one run — it is the only way to get a baseline, and a partial run leaves you comparing evaluated agents against unknowns. Once there is a baseline, later runs can be scoped.",
    },
    {
      id: "setup-what-evaluation-checks",
      prompt: "What does evaluation check?",
      answer: [
        "For every access path it resolves **identity → agent → tool or service → data**, then checks that path against your governance controls.",
        "",
        "It produces policy gaps (a path no policy covers), risky access (a path covered but over-permissioned), and violations (a path a policy forbids). Each gap comes with a **Recommended Policy** you can review.",
      ].join("\n"),
    },
    {
      id: "setup-access-paths",
      prompt: "Show me my agent access paths.",
      answer:
        "The **Agent Access Flow** shape above is illustrative until evaluation runs — it shows the four hops TrustLogix maps, not your actual routes. After evaluation the same view fills in with your real paths and the governance state of each hop.",
    },
  ],
  fallback:
    "I can explain what evaluation checks and how it scopes, and describe the estate that was discovered. I cannot tell you whether anything is secure until an evaluation has run.",
};

const SETUP_EVALUATING_SCRIPT: GuardianScript = {
  opening: [
    "Evaluation is in progress.",
    "",
    "Findings appear as they land, so what is below is real but incomplete. An agent with no finding yet may simply not have been reached.",
  ].join("\n"),
  prompts: [
    {
      id: "setup-findings-so-far",
      prompt: "What issues have you found so far?",
      answer:
        "**Requires Attention** holds the findings from the paths evaluated so far, highest priority first. The list will grow until the run completes — an empty stretch means not yet reached, not clear.",
    },
    {
      id: "setup-explain-gap",
      prompt: "Explain this policy gap.",
      answer:
        "Open any finding and I can walk its access path, name which hop is uncovered, and explain the **Recommended Policy** that would close it. Reviewing and applying that policy stays your decision — I never apply one.",
    },
    {
      id: "setup-needs-attention",
      prompt: "Which agents need attention?",
      answer:
        "The findings below are grouped by the control they breach and name their affected agents. Once the run finishes, the **Governance Landscape** will show the full split across your business functions and platforms.",
    },
  ],
  fallback:
    "While evaluation runs I can explain the findings that have landed, walk an access path, and describe a recommended policy. I cannot give you a complete picture until the run finishes.",
};

/**
 * One script per context kind, with `{name}` standing in for the subject. This
 * is what answers the long tail — every business unit, platform, resource and
 * agent that is not individually scripted below.
 */
const DEFAULT_BY_KIND: Record<GuardianContextKind, GuardianScript> = {
  estate: ESTATE_SCRIPT,
  // Reached only if a setup context arrives for a lifecycle state BY_KEY does
  // not name. ACTIVE is deliberately absent from both: once there is an
  // evaluated estate, Guardian's existing contextual scripts take over.
  setup: SETUP_ZERO_DAY_SCRIPT,
  "business-function": {
    opening: [
      "I can help you understand the {name} AI estate.",
      "",
      "{name} agents are grouped here by the business function that owns them. The card shows how many satisfy their required policies and how many have a recommendation waiting.",
      "",
      "You can investigate the highest-priority issues, the affected agents, or the recommended policies.",
    ].join("\n"),
    prompts: [
      {
        id: "bf-first",
        prompt: "What should I address first?",
        answer:
          "Address the agents marked Not Secure that already have a recommended policy — those are the ones where the fix is known and scoped. Agents with no recommendation need a custom policy and take longer.",
      },
      {
        id: "bf-why",
        prompt: "Why does {name} have issues?",
        answer:
          "Most issues in {name} come from agents holding broader data access than their observed usage requires. TrustLogix compares what an agent *can* reach against what it actually reads, and flags the difference.",
      },
      {
        id: "bf-agents",
        prompt: "Which {name} agents require attention?",
        answer:
          'Select **View Agents** on the {name} card to open the estate list filtered to exactly this group, then narrow it to **Not Secure** to see only the agents with an outstanding gap.',
      },
      {
        id: "bf-policy",
        prompt: "Which policy would reduce the most risk?",
        answer:
          "A least-privilege access policy scoped to {name} usually reduces the most risk per change, because it addresses the over-authorization pattern behind most of the group's findings at once.",
      },
    ],
    fallback:
      "I can explain why {name} has the issues it does, which agents are affected, and which policy would reduce the most risk. Ask about any of those.",
  },
  "ai-platform": {
    opening: [
      "I can help you understand the agents running on {name}.",
      "",
      "These agents share a platform, so they tend to share an identity model and a discovery path — which means they often share the same class of policy gap.",
    ].join("\n"),
    prompts: [
      {
        id: "plat-first",
        prompt: "What should I address first?",
        answer:
          "Platform-wide gaps are usually worth fixing before per-agent ones: a single policy scoped to {name} covers every agent discovered there, including the ones onboarded next.",
      },
      {
        id: "plat-identity",
        prompt: "How do {name} agents authenticate?",
        answer:
          "{name} agents use a mix of dedicated and delegated identities. Delegated agents inherit a user's access, which is why they most often show as over-authorized.",
      },
      {
        id: "plat-agents",
        prompt: "Which {name} agents require attention?",
        answer:
          "Select **View Agents** to open the estate list filtered to {name}, then narrow to **Not Secure**.",
      },
    ],
    fallback:
      "I can explain how {name} agents authenticate, what they can reach, and which policy would cover the platform as a whole.",
  },
  "data-platform": {
    opening: [
      "I can help you understand which agents reach {name}.",
      "",
      "This is the connected-resource view: every agent here has {name} among the resources TrustLogix observed it using.",
    ].join("\n"),
    prompts: [
      {
        id: "data-first",
        prompt: "What should I address first?",
        answer:
          "Start with agents reading sensitive datasets on {name} without a policy scoping that access — those carry the clearest regulated-data exposure.",
      },
      {
        id: "data-what",
        prompt: "What data is involved?",
        answer:
          "{name} holds a mix of enterprise and sensitive datasets. TrustLogix classifies each one, so a policy can restrict the sensitive subset without blocking ordinary analytics.",
      },
      {
        id: "data-policy",
        prompt: "Which policy would reduce the most risk?",
        answer:
          "A least-privilege policy restricting {name} access to observed usage. It removes standing access nobody exercises while leaving working paths intact.",
      },
    ],
    fallback:
      "I can explain which agents reach {name}, what data is involved, and which policy would scope that access.",
  },
  agent: {
    opening:
      "I can help you understand **{name}** — what it can reach, what it actually uses, and whether its required policies are in place.",
    prompts: [
      {
        id: "agent-access",
        prompt: "What data can this agent reach?",
        answer:
          "{name}'s observed access is listed on its detail page, alongside the resources it can reach but has never used — the gap between those two is what a least-privilege policy closes.",
      },
      {
        id: "agent-status",
        prompt: "Why is this agent not secure?",
        answer:
          "{name} is missing one or more of the access policies its data classification requires. The agent's Policy Coverage tab names which control is outstanding.",
      },
    ],
    fallback:
      "I can explain what {name} reaches, why it carries the status it does, and what policy would address it.",
  },
  risk: {
    opening: [
      "You're investigating **{name}**.",
      "",
      "I can explain why this was identified, show the affected agents, or explain the recommended policy.",
    ].join("\n"),
    prompts: [
      {
        id: "risk-why",
        prompt: "Why was this detected?",
        answer:
          "TrustLogix observed runtime access that crosses the boundary this control defines. Detection is based on what the agents actually did, not on configuration alone — which is why the finding names specific agents.",
      },
      {
        id: "risk-agents",
        prompt: "Which agents are affected?",
        answer:
          "The affected agents are listed on the issue's review panel, with the business unit and application each belongs to. Open **Review** to see them and to narrow the scope before any policy is applied.",
      },
      {
        id: "risk-data",
        prompt: "What data is involved?",
        answer:
          "Regulated and sensitive datasets, classified by TrustLogix during discovery. The review panel names each dataset and the compliance framework that governs it.",
      },
      {
        id: "risk-ignore",
        prompt: "What happens if I don't address this?",
        answer:
          "The access continues, and every occurrence is recorded. For regulated data this accumulates audit exposure rather than staying flat — the finding does not resolve on its own.",
      },
      {
        id: "risk-fix",
        prompt: "How does the recommended policy fix it?",
        answer:
          "It constrains access at runtime rather than removing the agent: requests inside the approved boundary continue to succeed, and requests outside it are denied and logged.",
      },
    ],
    fallback:
      "I can explain why **{name}** was detected, which agents and data are involved, what happens if it is left, and how the recommended policy addresses it.",
  },
  recommendation: {
    opening: [
      "You're looking at **{name}**.",
      "",
      "I can explain why this policy is recommended, which agents it affects, whether it could block legitimate access, and what the alternatives are.",
      "",
      "I do not apply policies — when you are ready, review it and decide.",
    ].join("\n"),
    prompts: [
      {
        id: "rec-why",
        prompt: "Why are you recommending this policy?",
        answer:
          "It addresses the largest share of the current exposure with the smallest change to working access paths. The review panel shows the alternatives considered and why this one scored highest.",
      },
      {
        id: "rec-agents",
        prompt: "Which agents will it affect?",
        answer:
          "The default scope is listed on the review panel, and you can narrow it by business unit, application or data source before deciding. The agent count updates as you do.",
      },
      {
        id: "rec-block",
        prompt: "Could this block legitimate access?",
        answer:
          "It restricts access outside the approved boundary. Access inside it is unaffected, and the review panel lists explicitly what would be blocked and what would continue to be allowed.",
      },
      {
        id: "rec-alternatives",
        prompt: "Are there alternative policies?",
        answer:
          "Yes — the review panel compares the alternatives side by side, including their business disruption and compliance coverage, so you can override the recommendation with reasons visible.",
      },
      {
        id: "rec-conditions",
        prompt: "What conditions would be applied?",
        answer:
          "Conditions on identity, data classification and location, evaluated at runtime for every request rather than at configuration time.",
      },
    ],
    fallback:
      "I can explain why **{name}** is recommended, who it affects, what it would block, and what the alternatives are.",
  },
};

/**
 * One governed data platform. `{name}` is substituted, so the four platforms
 * share a script: the questions a reader asks of Snowflake are the questions
 * they ask of Oracle, and the numbers come from the cards they just read
 * rather than being restated here.
 */
const DATA_PLATFORM_GOVERNANCE_SCRIPT: GuardianScript = {
  opening:
    "**{name}** is one of your governed data platforms. I can explain which of its data sources are incomplete, why access controls failed, and which agents reach it.",
  prompts: [
    {
      id: "platform-incomplete",
      prompt: "Which data sources are incomplete?",
      answer:
        "A data source is **Incomplete** when its governance setup is unfinished — a monitoring policy that never evaluated, or a sensitive-data policy that does not cover every connected agent. The **{name}** card lists each one with the specific gap. Incomplete is about setup, not about a control that ran and failed.",
    },
    {
      id: "platform-failed-controls",
      prompt: "Why did these access controls fail?",
      answer:
        "Failed access controls on **{name}** are controls that were deployed and did not apply — most often a role or grant that no longer resolves at the target. They are counted separately from Incomplete data sources on purpose: a control can fail on a data source whose setup is complete.",
    },
    {
      id: "platform-agents",
      prompt: "Which agents access {name}?",
      answer:
        "The platform drawer shows how many agents reach **{name}** and how many MCP servers provide that access. **View Agent Access Flows** opens those paths filtered to this platform, so you can see each agent's route and where its policy is missing.",
    },
    {
      id: "platform-disabled-policies",
      prompt: "What do the disabled monitoring policies mean?",
      answer:
        "A disabled monitoring policy is authored but not evaluating, so activity against **{name}** is not being recorded against it. Enabling one is a configuration change; I can tell you which are disabled, but I do not change policy state.",
    },
  ],
  fallback:
    "I can explain **{name}**'s incomplete data sources, its failed access controls, its disabled monitoring policies, and which agents reach it.",
};

/** Subjects worth scripting by hand, keyed exactly as the board keys them. */
const BY_KEY: Record<string, GuardianScript> = {
  estate: ESTATE_SCRIPT,
  "setup:ZERO_DAY": SETUP_ZERO_DAY_SCRIPT,
  "setup:DISCOVERING": SETUP_DISCOVERING_SCRIPT,
  "setup:DISCOVERED": SETUP_DISCOVERED_SCRIPT,
  "setup:EVALUATING": SETUP_EVALUATING_SCRIPT,
  // Every governed platform shares one script; `{name}` does the rest.
  ...Object.fromEntries(
    ["snowflake", "databricks", "oracle", "sqlserver"].map(id => [
      `data-platform:${id}`,
      DATA_PLATFORM_GOVERNANCE_SCRIPT,
    ]),
  ),
};

const render = (template: string, name: string): string =>
  template.replace(/\{name\}/g, name);

const UNSUPPORTED_CONTEXT_SCRIPT: GuardianScript = {
  opening: "You're now asking about **{name}**. Detailed evidence is not yet available for this resource.",
  prompts: [],
  fallback: "I don't yet have detailed evidence for **{name}**. Return to AI Estate or select an available agent or category to investigate.",
};

const scriptFor = (
  contextKey: string,
  contextKind: GuardianContextKind,
): GuardianScript =>
  BY_KEY[contextKey] ?? DEFAULT_BY_KIND[contextKind] ?? UNSUPPORTED_CONTEXT_SCRIPT;

export const guardianPromptsFor = (
  contextKey: string,
  contextKind: GuardianContextKind,
  label: string,
) =>
  scriptFor(contextKey, contextKind).prompts.map((prompt, index) => ({
    id: prompt.id,
    prompt: render(prompt.prompt, label),
    displayOrder: index,
  }));

/**
 * Answers one question. Never returns `undefined`: an unknown context falls
 * through to its kind's default and then to the estate script, because a
 * `undefined` from a mock query is rejected by React Query and would surface
 * as a failed drawer rather than a generic answer.
 */
export const guardianReplyFor = (
  contextKey: string,
  contextKind: GuardianContextKind,
  promptId: string | null,
  query: string,
  label: string,
): GuardianScriptedReply => {
  const script = scriptFor(contextKey, contextKind);
  const body = (() => {
    if (promptId === null && !query) return script.opening;
    const byId = script.prompts.find(prompt => prompt.id === promptId);
    if (byId) return byId.answer;
    // Free text: first prompt sharing a significant word with the question.
    const needle = query.toLowerCase();
    const byText = script.prompts.find(prompt =>
      prompt.prompt
        .toLowerCase()
        .split(/\W+/)
        .filter(word => word.length > 4)
        .some(word => needle.includes(word)),
    );
    return byText?.answer ?? script.fallback;
  })();

  return {
    id: `${contextKey}:${promptId ?? "opening"}:${body.length}`,
    contextKey,
    markdown: render(body, label),
    suggestions: guardianPromptsFor(contextKey, contextKind, label)
      .filter(prompt => prompt.id !== promptId && prompt.prompt.toLowerCase() !== query.toLowerCase())
      .slice(0, query ? 3 : 4),
  };
};
